import { useEffect, useRef, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import QRCode from "react-qr-code";
import { Crown, Printer, ArrowLeft, Eye, EyeOff, Soup, Camera, Trash2, Save, CalendarClock, Bookmark, X } from "lucide-react";
import { toast } from "sonner";
import {
  getBoard, getParty, getResults, revealWinner,
  updateParty, resetParty, uploadWinnerPhoto, clearWinnerPhoto, winnerPhotoUrl,
  savePreset, getPresets, applyPreset, deletePreset,
} from "../lib/api";

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-");

const PARTY_FIELDS = [
  ["title", "Party Title"],
  ["date_display", "Date"],
  ["time_display", "Tasting Begins"],
  ["location", "Location Name"],
  ["location_note", "Street Address"],
  ["location_city", "City, State Zip"],
];

export default function HostQR() {
  const [soups, setSoups] = useState([]);
  const [party, setParty] = useState(null);
  const [results, setResults] = useState({ results: [], total: 0 });
  const [presets, setPresets] = useState([]);
  const [form, setForm] = useState(null);
  const [presetName, setPresetName] = useState("");
  const [busy, setBusy] = useState(false);
  const photoRef = useRef(null);

  const refresh = useCallback(() => {
    getBoard().then((b) => setSoups(b.soups)).catch(() => {});
    getParty().then(setParty).catch(() => {});
    getResults().then(setResults).catch(() => {});
    getPresets().then((r) => setPresets(r.presets)).catch(() => {});
  }, []);

  useEffect(() => {
    refresh();
    const interval = setInterval(refresh, 10000);
    return () => clearInterval(interval);
  }, [refresh]);

  useEffect(() => {
    if (party && !form) {
      setForm({
        title: party.title || "",
        eyebrow: party.eyebrow || "",
        date_display: party.date_display || "",
        time_display: party.time_display || "",
        location: party.location || "",
        location_note: party.location_note || "",
        location_city: party.location_city || "",
        why: party.why || "",
      });
    }
  }, [party, form]);

  const uniqueSoups = soups.filter((s, i, arr) => arr.findIndex((x) => x.soup.toLowerCase() === s.soup.toLowerCase()) === i);
  const voteUrl = (soup) => `${window.location.origin}/vote/${encodeURIComponent(soup)}`;
  const leader = results.results[0];
  const revealed = Boolean(party?.winner_revealed);

  const run = async (fn, successMsg) => {
    if (busy) return;
    setBusy(true);
    try {
      await fn();
      if (successMsg) toast.success(successMsg);
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong — try again");
    } finally {
      setBusy(false);
    }
  };

  const toggleReveal = () => run(() => revealWinner(!revealed), revealed ? "Winner banner hidden" : "The Golden Ladle banner is live!");

  const saveParty = () =>
    run(async () => {
      await updateParty(form);
    }, "Party details saved — the page is updated");

  const doReset = () => {
    if (!window.confirm("This clears ALL RSVPs and votes so you can start the next party fresh. Saved presets are kept. Really reset?")) return;
    run(resetParty, "Fresh slate — RSVPs and votes cleared");
  };

  const pickWinnerPhoto = () => photoRef.current?.click();
  const onWinnerPhoto = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    run(() => uploadWinnerPhoto(file), "Glory shot added to the winner banner");
    e.target.value = "";
  };

  const doSavePreset = () => {
    if (!presetName.trim()) {
      toast.error("Give the preset a name first");
      return;
    }
    run(() => savePreset(presetName.trim()), `Preset "${presetName.trim()}" saved`);
    setPresetName("");
  };

  const printCards = () => {
    if (uniqueSoups.length === 0) return;
    const cards = uniqueSoups
      .map((entry) => {
        const qr = `https://api.qrserver.com/v1/create-qr-code/?size=220x220&color=2E4028&bgcolor=FFFFFF&data=${encodeURIComponent(voteUrl(entry.soup))}`;
        return `<div class="card"><img src="${qr}" alt="QR code"/><div class="name">${entry.soup}</div><div class="hint">scan to vote for this soup</div></div>`;
      })
      .join("");
    const w = window.open("", "_blank", "width=800,height=700");
    if (!w) return;
    w.document.write(`<html><head><title>Tasting Ballot QR Cards</title>
      <link href="https://fonts.googleapis.com/css2?family=Fraunces:ital,wght@0,700;1,600&family=Jost:wght@400;500&display=swap" rel="stylesheet">
      <style>
        body{font-family:'Jost',sans-serif;padding:24px;color:#26201A;}
        h1{font-family:'Fraunces',serif;font-size:26px;margin:0 0 6px;}
        .sub{font-size:12px;color:#5C4A33;margin-bottom:24px;}
        .grid{display:grid;grid-template-columns:1fr 1fr;gap:24px;}
        .card{border:2px solid #2E4028;padding:24px;text-align:center;page-break-inside:avoid;}
        .name{font-family:'Fraunces',serif;font-style:italic;font-weight:600;font-size:26px;color:#C24E21;margin-top:12px;}
        .hint{font-size:10px;text-transform:uppercase;letter-spacing:2px;color:#5C4A33;margin-top:6px;}
      </style></head>
      <body><h1>Soup Party &middot; Tasting Ballot</h1><p class="sub">Cut out each card and tape it beside its pot on the tasting table. Guests scan the code by the soup they loved.</p><div class="grid">${cards}</div>
      <script>window.onload=function(){setTimeout(function(){window.print();},800);};<\/script></body></html>`);
    w.document.close();
  };

  return (
    <div className="min-h-screen bg-[#A3B18A] px-6 md:px-16 py-16" data-testid="host-page">
      <div className="max-w-5xl mx-auto">
        <Link to="/" data-testid="host-back-link" className="inline-flex items-center gap-2 font-body text-xs uppercase tracking-[0.25em] text-fog hover:text-ember transition-colors duration-300">
          <ArrowLeft size={14} /> Back to the party page
        </Link>

        <p className="mt-10 font-body text-[11px] uppercase tracking-[0.35em] text-harvest">Host Only — bookmark this, don't share it</p>
        <h1 className="font-display text-4xl md:text-5xl text-cream mt-3">Party Control</h1>

        <div className="mt-12 border-2 border-harvest bg-moss p-8 md:p-10 shadow-[8px_8px_0_0_#2E4028]" data-testid="golden-ladle-card">
          <div className="flex items-center gap-3 mb-5">
            <Crown className="text-harvest" size={26} />
            <h2 className="font-display text-2xl md:text-3xl text-cream">The Golden Ladle</h2>
          </div>
          {leader ? (
            <p className="font-body text-base text-fog">
              Current leader: <span className="font-display italic text-xl text-ember">{leader.soup}</span> with {leader.count} vote{leader.count === 1 ? "" : "s"} ({results.total} cast)
            </p>
          ) : (
            <p className="font-body text-base text-fog">No votes yet — results appear here as they come in.</p>
          )}
          <div className="mt-6 flex flex-wrap items-center gap-4">
            <button data-testid="reveal-toggle-btn" onClick={toggleReveal} disabled={busy} className="btn-ember disabled:opacity-40">
              {revealed ? <EyeOff size={16} /> : <Eye size={16} />}
              {revealed ? "Hide the Winner Banner" : "Reveal the Winner"}
            </button>
            <p className="font-body text-xs uppercase tracking-[0.2em] text-fog">
              {revealed ? "Banner is LIVE on the party page" : "Banner hidden — guests just see live results"}
            </p>
          </div>
          <div className="mt-6 border-t border-forest/30 pt-6 flex flex-wrap items-center gap-4">
            <input ref={photoRef} type="file" accept="image/*" className="hidden" data-testid="winner-photo-input" onChange={onWinnerPhoto} />
            <button data-testid="winner-photo-btn" onClick={pickWinnerPhoto} disabled={busy} className="btn-ghost !px-6 !py-3 !text-xs">
              <Camera size={16} />
              {party?.winner_photo_path ? "Replace Glory Shot" : "Add a Glory Shot"}
            </button>
            {party?.winner_photo_path && (
              <>
                <img src={winnerPhotoUrl(party.winner_photo_path)} alt="Winner glory shot" className="h-16 w-16 object-cover border-2 border-forest rotate-2" data-testid="winner-photo-preview" />
                <button
                  data-testid="winner-photo-remove"
                  onClick={() => run(clearWinnerPhoto, "Glory shot removed")}
                  disabled={busy}
                  className="font-body text-xs uppercase tracking-[0.2em] text-berry hover:text-cream transition-colors duration-300"
                >
                  Remove
                </button>
              </>
            )}
            <p className="font-body text-xs text-fog/70 w-full">Optional — the winning soup's photo appears on the winner banner.</p>
          </div>
        </div>

        <div className="mt-8 border border-forest bg-moss p-8 md:p-10" data-testid="next-party-card">
          <div className="flex items-center gap-3 mb-6">
            <CalendarClock className="text-ember" size={24} />
            <h2 className="font-display text-2xl md:text-3xl text-cream">Next Party</h2>
          </div>
          {form && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-6">
              {PARTY_FIELDS.map(([key, label]) => (
                <div key={key}>
                  <label className="field-label" htmlFor={`party-${key}`}>{label}</label>
                  <input
                    id={`party-${key}`}
                    data-testid={`party-${key}-input`}
                    className="field"
                    value={form[key]}
                    onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))}
                  />
                </div>
              ))}
              <div className="md:col-span-2">
                <label className="field-label" htmlFor="party-why">Why We Gather</label>
                <textarea
                  id="party-why"
                  data-testid="party-why-input"
                  className="field min-h-[80px] resize-y"
                  value={form.why}
                  onChange={(e) => setForm((f) => ({ ...f, why: e.target.value }))}
                />
              </div>
            </div>
          )}
          <div className="mt-8 flex flex-wrap items-center gap-4">
            <button data-testid="party-save-btn" onClick={saveParty} disabled={busy || !form} className="btn-ember disabled:opacity-40">
              <Save size={16} />
              Save Party Details
            </button>
            <button
              data-testid="reset-party-btn"
              onClick={doReset}
              disabled={busy}
              className="btn-ghost !border-berry !text-berry hover:!text-cream hover:!bg-berry"
            >
              <Trash2 size={16} />
              Reset RSVPs & Votes
            </button>
          </div>
          <p className="mt-4 font-body text-xs text-fog/70">Reset clears the guest list and votes for a fresh party — your saved presets and photos stay.</p>
        </div>

        <div className="mt-8 border border-forest bg-moss p-8 md:p-10" data-testid="presets-card">
          <div className="flex items-center gap-3 mb-6">
            <Bookmark className="text-harvest" size={24} />
            <h2 className="font-display text-2xl md:text-3xl text-cream">Party Presets</h2>
          </div>
          <p className="font-body text-sm text-fog mb-6">Save the current party details as a preset, then re-apply them anytime — perfect for recurring themes like the annual soup exchange.</p>
          <div className="flex gap-3 max-w-md">
            <input
              data-testid="preset-name-input"
              className="field"
              placeholder='e.g. "Fall Soup Exchange"'
              value={presetName}
              onChange={(e) => setPresetName(e.target.value)}
            />
            <button data-testid="preset-save-btn" onClick={doSavePreset} disabled={busy} className="btn-ember !px-6 shrink-0 disabled:opacity-40">
              <Save size={16} />
            </button>
          </div>
          {presets.length > 0 && (
            <ul className="mt-8 space-y-3" data-testid="preset-list">
              {presets.map((p) => (
                <li key={p.id} className="flex flex-wrap items-center gap-4 border border-forest/40 px-5 py-4" data-testid={`preset-row-${slug(p.name)}`}>
                  <div className="min-w-0">
                    <p className="font-display text-lg text-cream truncate">{p.name}</p>
                    <p className="font-body text-xs text-fog">{p.date_display} · {p.title}</p>
                  </div>
                  <div className="ml-auto flex items-center gap-3">
                    <button
                      data-testid={`preset-apply-${slug(p.name)}`}
                      onClick={() => run(() => applyPreset(p.id), `Preset "${p.name}" applied to the page`)}
                      disabled={busy}
                      className="btn-ghost !px-5 !py-2 !text-[11px]"
                    >
                      Apply
                    </button>
                    <button
                      data-testid={`preset-delete-${slug(p.name)}`}
                      onClick={() => run(() => deletePreset(p.id), "Preset deleted")}
                      disabled={busy}
                      className="text-fog hover:text-berry transition-colors duration-300"
                    >
                      <X size={16} />
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="mt-8 border border-forest bg-moss p-8 md:p-10" data-testid="host-qr-section">
          <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
            <div className="flex items-center gap-3">
              <Soup className="text-ember" size={24} />
              <h2 className="font-display text-2xl md:text-3xl text-cream">QR Cards for the Table</h2>
            </div>
            <button
              data-testid="host-print-qr-btn"
              onClick={printCards}
              disabled={uniqueSoups.length === 0}
              className="btn-ember disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <Printer size={16} />
              Print All Cards
            </button>
          </div>
          {uniqueSoups.length === 0 ? (
            <p className="font-accent text-3xl text-fog" data-testid="host-qr-empty">cards appear once the first soup is claimed</p>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5" data-testid="host-qr-grid">
              {uniqueSoups.map((entry) => (
                <div key={entry.soup} className="bg-[#FBF4E6] border border-forest p-5 text-center" data-testid={`host-qr-card-${slug(entry.soup)}`}>
                  <div className="bg-white inline-block p-2">
                    <QRCode value={voteUrl(entry.soup)} size={110} fgColor="#2E4028" bgColor="#FFFFFF" />
                  </div>
                  <p className="font-display italic text-xl text-ember mt-3 leading-tight">{entry.soup}</p>
                  <p className="font-body text-[10px] uppercase tracking-[0.2em] text-fog mt-1">scan to vote</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
